import { endMinute, startMinute, type Opportunity } from '../../shared/opportunity';

/**
 * Where a block should be *drawn* as ending, so the block starting right after
 * it reads as a separate block.
 *
 * An event ending at 6:00 and one starting at 6:00 share an edge, and two
 * parchment cards sharing an edge are one parchment card as far as the eye is
 * concerned. This carves a seam by shortening the earlier one — never the
 * later one, which would misreport a start time, and never the stored times,
 * which are what the card says in words.
 *
 * `gap` is in minutes, and each view passes whatever its own scale makes worth
 * about five pixels: three minutes down the day column, ten across a week.
 */
export function seamEnd(end: number, starts: Iterable<number>, gap: number): number {
  let next = Infinity;
  for (const start of starts) {
    if (start >= end && start < next) next = start;
  }
  if (next === Infinity || next - end >= gap) return end;
  return end - (gap - (next - end));
}

export interface Cluster {
  /** Events that mutually overlap, in start order. */
  events: Opportunity[];
  startMinutes: number;
  endMinutes: number;
}

/**
 * Groups events into runs of mutual overlap.
 *
 * There is no cap on how many share a run. Clusters of four or more used to
 * collapse into a stacked list, on the reasoning that three columns is as many
 * as can carry a title — but that traded away the one thing the day view is
 * for. Six events colliding is a *shape*: which two start together, which one
 * runs through all of them, where the only clear half-hour is. A stack throws
 * that away and replaces it with a list the user could have read anywhere.
 *
 * So they stay side by side however thin they get, and the card drops its text
 * below the width where text was going to be unreadable anyway.
 */
export function groupClusters(events: Opportunity[], defaultDuration = 60): Cluster[] {
  const endOf = (e: Opportunity) => endMinute(e) ?? (startMinute(e) ?? 0) + defaultDuration;
  const sorted = [...events].sort((a, b) => (startMinute(a) ?? 0) - (startMinute(b) ?? 0));

  const clusters: Cluster[] = [];
  let current: Opportunity[] = [];
  let currentEnd = -1;

  const flush = () => {
    if (current.length === 0) return;
    clusters.push({
      events: current,
      startMinutes: startMinute(current[0]) ?? 0,
      endMinutes: currentEnd,
    });
    current = [];
    currentEnd = -1;
  };

  for (const e of sorted) {
    const start = startMinute(e) ?? 0;
    if (current.length > 0 && start >= currentEnd) flush();
    current.push(e);
    currentEnd = Math.max(currentEnd, endOf(e));
  }
  flush();

  return clusters;
}

/**
 * A thing occupying a span of the day. An event or a travel block — the layout
 * does not care which, and must not, because they compete for the same width.
 */
export interface Span {
  id: string;
  start: number;
  end: number;
}

export interface PlacedSpan<T extends Span> {
  item: T;
  lane: number;
  /** Lanes in this item's cluster. Shared, so left edges line up. */
  lanes: number;
}

/**
 * Lays spans side by side wherever they overlap.
 *
 * The same routine as `assignLanes`, generalised off events so a travel
 * block can take a lane too. That is the whole point of the reference image:
 * a journey that runs into the next event is drawn beside it, not over it, so
 * the collision is visible as a shape rather than described in words.
 *
 * Clusters share a lane count so their left edges align down the column. A
 * cluster of one keeps full width.
 */
export function layoutSpans<T extends Span>(
  spans: T[],
  /**
   * Lower goes further left. Items are placed in this order, so a whole class
   * of item can be given the left lanes regardless of when it starts.
   *
   * Travel blocks use it. Without it a block took whichever lane fell out of
   * the scan, which changed as it was dragged across an event's start — so the
   * thing under the cursor jumped from one side of the column to the other
   * mid-drag. A block that stays put while you move it is worth more than a
   * perfectly packed column.
   */
  priority: (item: T) => number = () => 0,
): PlacedSpan<T>[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: PlacedSpan<T>[] = [];

  let cluster: T[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;

    /**
     * Lanes hold the spans placed in them rather than just their latest end.
     * Once items are placed out of start order, "does this lane end before I
     * begin" is no longer the right question — "does anything in this lane
     * overlap me" is.
     */
    const lanes: T[][] = [];
    const order = [...cluster].sort(
      (a, b) => priority(a) - priority(b) || a.start - b.start || a.end - b.end,
    );

    const placed = order.map((item) => {
      let lane = lanes.findIndex(
        (held) => !held.some((other) => other.start < item.end && other.end > item.start),
      );
      if (lane === -1) {
        lane = lanes.length;
        lanes.push([]);
      }
      lanes[lane].push(item);
      return { item, lane };
    });

    for (const p of placed) out.push({ ...p, lanes: lanes.length });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const span of sorted) {
    if (cluster.length > 0 && span.start >= clusterEnd) flush();
    cluster.push(span);
    clusterEnd = Math.max(clusterEnd, span.end);
  }
  flush();

  return out;
}
